import React, { useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Container,
} from '@material-ui/core'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import GolfCourseIcon from 'components/material-symbol/icons/GolfCourse'
import { useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { StatusChip } from '../components'
import { GET_MY_HOLE_IN_ONES } from './query'
import useStyles from './styles'

const ITEMS_PER_PAGE = 10

const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

const AchievementsListPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const [page, setPage] = useState(0)

  const { data, loading, error } = useQuery(GET_MY_HOLE_IN_ONES, {
    fetchPolicy: 'cache-and-network',
  })

  const achievements = data?.myHoleInOnes || []
  const totalItems = achievements.length
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
  const startIndex = page * ITEMS_PER_PAGE
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems)
  const paginatedAchievements = achievements.slice(startIndex, endIndex)

  const handleRowClick = id => {
    history.push(`/achievements/${id}`)
  }

  const handlePrevPage = () => {
    setPage(prev => Math.max(0, prev - 1))
  }

  const handleNextPage = () => {
    setPage(prev => Math.min(totalPages - 1, prev + 1))
  }

  const renderContent = () => {
    if (loading && !data) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )
    }

    if (error) {
      return (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>
            Error loading achievements
          </Typography>
          <Typography className={classes.emptyStateText}>
            Please try again later.
          </Typography>
        </Box>
      )
    }

    if (achievements.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <GolfCourseIcon className={classes.emptyStateIcon} />
          <Typography className={classes.emptyStateTitle}>
            No achievements yet
          </Typography>
          <Typography className={classes.emptyStateText}>
            Your verified hole-in-ones will appear here.
          </Typography>
        </Box>
      )
    }

    return (
      <>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                Golf Course
              </TableCell>
              <TableCell className={classes.tableHeadCell}>Date</TableCell>
              <TableCell className={classes.tableHeadCell}>Hole</TableCell>
              <TableCell className={classes.tableHeadCell}>Par</TableCell>
              <TableCell className={classes.tableHeadCell}>Distance</TableCell>
              <TableCell className={classes.tableHeadCell} align="right">
                Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAchievements.map(achievement => (
              <TableRow
                key={achievement.id}
                className={classes.tableRow}
                onClick={() => handleRowClick(achievement.id)}
              >
                <TableCell className={classes.tableCell}>
                  <Box className={classes.courseCell}>
                    <Box className={classes.courseIconContainer}>
                      <GolfCourseIcon className={classes.courseIcon} />
                    </Box>
                    <Box className={classes.courseInfo}>
                      <Box className={classes.courseNameContainer}>
                        <Typography className={classes.courseName}>
                          {achievement.courseName}
                        </Typography>
                        <StatusChip
                          status={
                            achievement.status ||
                            (achievement.publicVisibility
                              ? 'PUBLISHED'
                              : 'DRAFT')
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Typography className={classes.dateCell}>
                    {formatDate(achievement.occurredAt)}
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box className={classes.holeCell}>
                    #{achievement.holeNumber}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Typography className={classes.parCell}>
                    {achievement.holePar}
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Typography className={classes.distanceCell}>
                    {achievement.yardage} Yds
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell} align="right">
                  <ChevronRightIcon className={classes.chevronIcon} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {totalItems > 0 && (
          <Box className={classes.pagination}>
            <Typography className={classes.paginationText}>
              Showing {startIndex + 1} to {endIndex} of {totalItems}{' '}
              achievements
            </Typography>
            <Box className={classes.paginationButtons}>
              <IconButton
                className={classes.paginationButton}
                onClick={handlePrevPage}
                disabled={page === 0}
                size="small"
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                className={classes.paginationButton}
                onClick={handleNextPage}
                disabled={page >= totalPages - 1}
                size="small"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </>
    )
  }

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography className={classes.title}>Achievements</Typography>
          <Typography className={classes.subtitle}>
            A list of all your Verified PGA Aces
          </Typography>
        </Box>

        <TableContainer component={Paper} className={classes.tableContainer}>
          {renderContent()}
        </TableContainer>
      </Box>
    </Container>
  )
}

export default AchievementsListPage
